/** Pure helpers for entity linkification (no React / markdown deps). */

export const UUID_RE =
  /\b[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}\b/gi;

const EMAIL_RE = /\b[^\s@]+@[^\s@]+\.[^\s@]+\b/g;

export const COLUMN_ENTITY_KIND = {
  run_id: "run",
  runid: "run",
  run: "run",
  span_id: "span",
  spanid: "span",
  span: "span",
  distinct_id: "user",
  distinctid: "user",
  user_id: "user",
  userid: "user",
  user: "user",
  email: "user",
  session_id: "session",
  sessionid: "session",
  issue_id: "issue",
  issueid: "issue",
  event_id: "event",
  eventid: "event",
  group_id: "group",
  groupid: "group",
};

export function normColKey(s) {
  return String(s || "")
    .toLowerCase()
    .replace(/-/g, "_")
    .replace(/\s+/g, "_");
}

export function entityKindForColumn(col) {
  if (!col || typeof col !== "object") return null;
  if (col.entity) return col.entity;
  const fromKey = COLUMN_ENTITY_KIND[normColKey(col.key)];
  if (fromKey) return fromKey;
  return COLUMN_ENTITY_KIND[normColKey(col.label)] || null;
}

function overlaps(a, b) {
  return a.index < b.index + b.length && b.index < a.index + a.length;
}

// A UUID in prose carries no inherent kind. This used to assume every one was
// a run, which produced links to things that do not exist: an answer reading
//   "error message `cb31bda6-…: max retry exceeded`"
// linked that id — an internal request id from the provider's error string — as
// trodo://entity/run/cb31bda6-…, and clicking it 404'd. Users reported it as
// "clicking span ids shows an error".
//
// So only link when the text SAYS what the id is. The kind word sits within a
// few tokens before the id in every real reference the analyst writes:
// "in the agentic run <id>", "failing span <id>", "session <id>".
const KIND_CUES = [
  [/\b(?:run|trace)\b[^A-Za-z0-9]{0,12}$/i, "run"],
  [/\bspans?\b[^A-Za-z0-9]{0,12}$/i, "span"],
  [/\bsessions?\b[^A-Za-z0-9]{0,12}$/i, "session"],
  [/\bissues?\b[^A-Za-z0-9]{0,12}$/i, "issue"],
  [/\b(?:users?|distinct[_ ]?id)\b[^A-Za-z0-9]{0,12}$/i, "user"],
  [/\bevents?\b[^A-Za-z0-9]{0,12}$/i, "event"],
];

/** The entity kind the surrounding prose declares, or null when it does not. */
function kindFromContext(text, index) {
  const before = text.slice(Math.max(0, index - 48), index);
  for (const [re, kind] of KIND_CUES) if (re.test(before)) return kind;
  return null;
}

export function findEntityTokens(text) {
  if (!text) return [];
  const tokens = [];
  let m;
  const emailRe = new RegExp(EMAIL_RE.source, "g");
  while ((m = emailRe.exec(text)) !== null) {
    tokens.push({ index: m.index, length: m[0].length, kind: "user", id: m[0] });
  }
  const uuidRe = new RegExp(UUID_RE.source, "gi");
  while ((m = uuidRe.exec(text)) !== null) {
    const kind = kindFromContext(text, m.index);
    if (!kind) continue; // unlabelled id: plain text beats a link that 404s
    const hit = { index: m.index, length: m[0].length, kind, id: m[0] };
    if (!tokens.some((t) => overlaps(t, hit))) tokens.push(hit);
  }
  tokens.sort((a, b) => a.index - b.index);
  return tokens;
}
